import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type {
  ActivityResponse,
  PaginatedResponse,
} from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

const normalizeGroupedActivityActors = (
  activity: ActivityResponse,
): ActivityResponse => {
  if (
    activity.itemType !== "group" ||
    !Array.isArray(activity.actors) ||
    activity.actors.length === 0
  ) {
    return activity;
  }

  const uniqueActorMap = new Map<number, (typeof activity.actors)[number]>();
  for (const actor of activity.actors) {
    uniqueActorMap.set(actor.id, actor);
  }

  const uniqueActors = Array.from(uniqueActorMap.values());
  return {
    ...activity,
    actors: uniqueActors.slice(0, 2),
    actorsCount: uniqueActors.length,
  };
};

export const activitiesApi = {
  getActivities: async (
    containerId: string | number,
    page = 1,
    perPage = 10,
  ): Promise<PaginatedResponse<ActivityResponse>> => {
    try {
      const response = await api.get<PaginatedResponse<ActivityResponse>>(
        `${API_URLS.ACTIVITIES}/container/${containerId}`,
        {
          params: {
            page,
            limit: perPage,
            aggregate: 1,
          },
        },
      );
      return {
        ...response.data,
        results: response.data.results.map((activity) =>
          normalizeGroupedActivityActors(activity),
        ),
      };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },
};
